import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { fetchClientServices, type ClientServiceListItem } from '../widget-api/client-services';
import { useAsync } from '../widget-hooks/useAsync';
import { useEmbedOrgBranch } from '../widget-hooks/useEmbedOrgBranch';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { pathService } from '../widget-lib/path-routes';
import { PromoBanner } from '../widget-ui/promo-banner';

function formatPrice(p: string | number): string {
  try {
    const n = typeof p === 'number' ? p : Number.parseFloat(p);
    if (Number.isFinite(n) && n > 0) {
      return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);
    }
  } catch { /* ignore */ }
  return '';
}

function formatDuration(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60} ч`;
  if (minutes > 60) return `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
  return `${minutes} мин`;
}

export default function ServicesListPage() {
  useWidgetSeo(
    'Услуги и цены',
    'Полный каталог услуг, направлений и прайс-лист. Подробное описание и онлайн-запись.'
  );
  const [, setSearchParams] = useSearchParams();
  const suffix = useQuerySuffix();
  const { organizationId, accessToken, bootstrapHint } = useEmbedBootstrap();
  const { widgetConfig, guestShowcaseOrg, effectiveBranchId } = useEmbedOrgBranch();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(effectiveBranchId);

  useEffect(() => {
    setSelectedBranchId(effectiveBranchId);
  }, [effectiveBranchId]);

  const orgId = widgetConfig?.organization_id ?? organizationId;
  const branches = widgetConfig?.branches ?? guestShowcaseOrg?.branches ?? [];

  const canLoad = orgId != null;

  const { data: services, loading, error } = useAsync(
    () => {
      if (!canLoad) return Promise.resolve([] as ClientServiceListItem[]);
      return fetchClientServices({ organizationId: orgId, branchId: effectiveBranchId, accessToken });
    },
    [orgId, effectiveBranchId, accessToken, canLoad]
  );

  const handleBranchChange = (branchIdVal: string) => {
    const nextId = branchIdVal ? Number(branchIdVal) : null;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextId != null) {
        next.set('branch_id', String(nextId));
      } else {
        next.delete('branch_id');
      }
      return next;
    });
  };

  const filteredServices = useMemo(() => {
    if (!services) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return services;
    return services.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      (s.description && s.description.toLowerCase().includes(q))
    );
  }, [services, searchQuery]);

  if (bootstrapHint) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Услуги</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (orgId == null || !canLoad) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Услуги</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Нет контекста организации. Откройте виджет по ссылке с org_slug и ключом или войдите как клиент.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-4 sm:px-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Услуги</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-[var(--text-muted)] pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по названию..."
              className="w-full rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] py-1.5 pl-8 pr-4 text-xs text-[var(--text)] outline-none transition-all focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            />
          </div>
          {branches.length > 1 && (
            <select
              value={selectedBranchId ?? ''}
              onChange={(e) => handleBranchChange(e.target.value)}
              className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-xs text-[var(--text)] outline-none transition-all focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            >
              <option value="">Все филиалы</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <PromoBanner screen="services" />
      {loading ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <li
              key={i}
              className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]"
            >
              <div className="space-y-2">
                <div className="h-4 w-40 animate-pulse rounded bg-[var(--bg-soft)]" />
                <div className="h-3 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
                <div className="h-3 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
              </div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      ) : !filteredServices.length ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/50 px-4 py-10 text-center shadow-[var(--shadow-card)]">
            <p className="text-sm text-[var(--text-muted)]">Нет активных услуг для отображения.</p>
          </div>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {filteredServices.map((s) => {
            const fromVal = typeof s.price_from === 'number' ? s.price_from : null;
            const baseVal = Number.parseFloat(String(s.price));
            const useFrom = fromVal != null && Number.isFinite(baseVal) && fromVal < baseVal;
            const priceStr = useFrom ? formatPrice(fromVal as number) : formatPrice(s.price);
            const priceLabel = priceStr && useFrom ? `от ${priceStr}` : priceStr;
            const trialVal =
              typeof s.trial_price === 'number' && Number.isFinite(s.trial_price) && s.trial_price >= 1
                ? s.trial_price
                : null;
            const durationStr = formatDuration(s.duration_minutes);
            return (
              <li key={s.id}>
                <Link
                  to={`${pathService(s.id)}${suffix}`}
                  className="flex min-w-0 flex-col gap-2 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)] transition-shadow duration-200 hover:shadow-md active:scale-[0.99]"
                >
                  <p className="text-sm font-semibold text-[var(--text)]">{s.name}</p>
                  {s.description ? (
                    <p className="line-clamp-2 text-xs leading-relaxed text-[var(--text-muted)]">{s.description}</p>
                  ) : null}
                  <div className="mt-auto flex items-center gap-2 pt-1">
                    <span className="rounded-full bg-[var(--bg-soft)] px-2.5 py-0.5 text-xs font-medium text-[var(--text-muted)]">
                      {durationStr}
                    </span>
                    {priceStr ? (
                      <span className="text-xs font-semibold text-[var(--accent)]">{priceLabel}</span>
                    ) : null}
                    {trialVal != null ? (
                      <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 text-xs font-semibold text-[var(--accent)]">
                        Пробная: {formatPrice(trialVal)}
                      </span>
                    ) : null}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
